"use client";

import { ScrollContainer } from "@/components/platform/scroll-container";
import { Badge } from "@/components/ui/badge";
import { useScrollToCenter } from "@/hooks/useScrollToCenter";
import { cn } from "@/lib/utils";
import { useTopicStore } from "@/store/topic";
import { ChevronDown } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import CryptoTimeSelect from "./crypto-time-select";

interface SubTopicTabsProps {
    showWeekFilter?: boolean;
}

export default function SubTopicTabs( { showWeekFilter = true }: SubTopicTabsProps = {} ) {
    const pathname = usePathname();
    const router = useRouter();
    const {
        normalTopicChains,
        liveTopicChains,
        setSelectedNormalTopic,
        setSelectedLiveTopic,
        sportTimeRange,
        setSportTimeRange,
    } = useTopicStore();
    const [ isWeekDropdownOpen, setIsWeekDropdownOpen ] = useState( false );
    const { scrollToCenter } = useScrollToCenter( { delay: 50 } );

    // 判断是否在 Live 页
    const isLivePage = pathname === "/live";

    const topicChains = useMemo( () => isLivePage ? liveTopicChains : normalTopicChains, [
        liveTopicChains,
        normalTopicChains,
        pathname,
    ] );

    function handlerSelect( topic ) {
        let target = topic;
        while ( target.children && target.children.length ) {
            target = target.children[0];
        }

        // 获取当前选中的 topic（topicChains 的最后一个元素）
        const currentSelectedTopic = topicChains[topicChains.length - 1];

        // 如果当前选中的 topic 和要选择的 topic 相同，不执行逻辑
        if ( currentSelectedTopic?.id === target.id ) {
            return;
        }

        isLivePage ? setSelectedLiveTopic( target ) : setSelectedNormalTopic( target );
    }

    // 处理 tab 点击事件
    function handleTabClick( e: React.MouseEvent<HTMLElement>, topic: any ) {
        handlerSelect( topic );
        const element = e.currentTarget;
        const scrollContainer = element.closest( ".overflow-x-auto" ) as HTMLDivElement;
        if ( scrollContainer ) {
            scrollToCenter( element, scrollContainer );
        }
    }
    const isCrypto = topicChains[0]?.name.toLocaleLowerCase() === "crypto";
    const isSports = topicChains[0]?.name.toLocaleLowerCase() === "sports";

    // 递归渲染子级 tabs
    function renderChildrenTabs( parentTopic, level ) {
        if ( !parentTopic?.children?.length ) return null;
        if ( parentTopic.children.length === 1 && !parentTopic.children[0]?.children?.length ) return null;

        const selectedChild = topicChains[level + 1];
        const isLastLevel = !selectedChild?.children?.length;
        const isSportTopic = selectedChild?.ifSport === true;
        if ( isCrypto || isSports ) {
            return (
                <CryptoTimeSelect
                    className={`${isCrypto ? "py-2 border-b border-stroke" : "p-0 mt-4 mb-4"}`}
                    list={parentTopic.children}
                    onClick={handleTabClick}
                    active={selectedChild}
                    isCrypto={isCrypto}
                />
            );
        }

        return (
            <>
                <div className={cn( "flex items-center mt-3", selectedChild?.children?.length > 0 ? "mb-3" : "" )}>
                    <div className="flex-1 min-w-0">
                        <ScrollContainer gap="gap-2">
                            {parentTopic.children.map( ( tp ) => {
                                const isActive = tp === selectedChild;

                                // 第一行使用 Badge 样式，其他行使用 button 样式
                                if ( level === 0 ) {
                                    return (
                                        <Badge
                                            className={`flex-shrink-0 h-8 px-2 py-1 text-xs font-medium rounded-md cursor-pointer transition-colors ${
                                                isActive
                                                    ? "bg-action-primary text-content-fixed-inverse"
                                                    : "text-t3 bg-surface-card"
                                            }`}
                                            key={tp.id}
                                            variant="secondary"
                                            onClick={( e ) => handleTabClick( e, tp )}
                                        >
                                            {tp.name}
                                        </Badge>
                                    );
                                }

                                return (
                                    <button
                                        key={tp.id}
                                        onClick={( e ) => handleTabClick( e, tp )}
                                        className={cn(
                                            "flex-shrink-0 px-2 h-8 text-xs font-medium rounded-lg cursor-pointer transition-all",
                                            isActive
                                                ? "bg-content-primary text-surface-page"
                                                : "bg-surface-card text-t1",
                                        )}
                                    >
                                        {tp.name}
                                    </button>
                                );
                            } )}
                        </ScrollContainer>
                    </div>

                    {/* 周筛选下拉按钮 - 仅在最后一层且是运动分类时显示 */}
                    {showWeekFilter && isLastLevel && isSportTopic && (
                        <div className="relative flex-shrink-0 ml-2 self-end">
                            <button
                                onClick={() => setIsWeekDropdownOpen( !isWeekDropdownOpen )}
                                className="flex items-center gap-1 px-2 h-8 rounded-lg bg-surface-card text-t1 text-xs font-medium"
                            >
                                {sportTimeRange === "past1w"
                                    ? "PastWeek1"
                                    : sportTimeRange === "past2w"
                                    ? "PastWeek2"
                                    : sportTimeRange === "past3w"
                                    ? "PastWeek3"
                                    : sportTimeRange === "upcoming"
                                    ? "Week1"
                                    : sportTimeRange === "upcoming2w"
                                    ? "Week2"
                                    : "Week3"}
                                <ChevronDown
                                    className={cn(
                                        "w-3.5 h-3.5 transition-transform",
                                        isWeekDropdownOpen && "rotate-180",
                                    )}
                                />
                            </button>

                            {/* 下拉菜单 */}
                            {isWeekDropdownOpen && (
                                <>
                                    {/* 遮罩层 */}
                                    <div
                                        className="fixed inset-0 z-40 max-w-[500px] mx-auto left-0 right-0"
                                        onClick={() => setIsWeekDropdownOpen( false )}
                                    />
                                    {/* 下拉内容 */}
                                    <div className="absolute right-0 top-full mt-1 z-50 bg-brand-button rounded-lg shadow-lg border border-stroke py-1 min-w-[100px]">
                                        {( [
                                            { value: "upcoming", label: "Week1" },
                                            { value: "upcoming2w", label: "Week2" },
                                            { value: "upcoming3w", label: "Week3" },
                                            { value: "past1w", label: "PastWeek1" },
                                            { value: "past2w", label: "PastWeek2" },
                                            { value: "past3w", label: "PastWeek3" },
                                        ] as const ).map( ( { value, label } ) => (
                                            <button
                                                key={value}
                                                onClick={() => {
                                                    setSportTimeRange( value );
                                                    setIsWeekDropdownOpen( false );
                                                }}
                                                className={cn(
                                                    "w-full px-3 py-2 text-left text-xs font-medium transition-colors",
                                                    sportTimeRange === value
                                                        ? "bg-surface-muted text-t1"
                                                        : "text-t3 hover:bg-surface-subtle",
                                                )}
                                            >
                                                {label}
                                            </button>
                                        ) )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* 递归渲染选中项的子级 */}
                {selectedChild && renderChildrenTabs( selectedChild, level + 1 )}
            </>
        );
    }

    return <div className="space-y-2">{topicChains[0] && renderChildrenTabs( topicChains[0], 0 )}</div>;
}
